import { createFileRoute, Link } from "@tanstack/react-router";
import { BLACK25, BLACK25_ABERTURA_POR_HORA, BLACK25_ABERTURA_DIA } from "@/lib/black2x1/historico-2025";
import { comparativoAteAgora } from "@/lib/black2x1/vendas-hora";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { LeadsTable, T, tag } from "@/components/black2x1/BlackLeadsTable";
import { AdminGate } from "@/components/admin/AdminGate";
import { getBlackDashboard, getBlackDiario } from "@/lib/black2x1/dashboard.functions";
import { BlackRitmoPanel } from "@/components/black2x1/BlackRitmoPanel";
import { BlackTorneiras } from "@/components/black2x1/BlackTorneiras";
import { BlackGruposConfig } from "@/components/black2x1/BlackGruposConfig";
import { BlackQuizzConfig } from "@/components/black2x1/BlackQuizzConfig";
import { Recolhivel } from "@/components/black2x1/Recolhivel";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, AreaChart, Area, PieChart, Pie, Cell, ComposedChart, Line, ReferenceLine } from "recharts";

const C = { purple: "var(--black-campaign-purple)", cyan: "var(--black-campaign-cyan)", lilac: "var(--black-campaign-lilac)", soft: "var(--black-campaign-soft)" };
const PIE = [C.cyan, C.purple, C.lilac, C.soft];
const tip = { contentStyle: { background: "var(--black-campaign-surface)", border: "1px solid var(--black-campaign-purple)", borderRadius: 8, fontSize: 12 }, labelStyle: { color: "var(--black-campaign-soft)" }, itemStyle: { color: "var(--black-campaign-soft)" } };
const axis = { stroke: "var(--black-campaign-soft)", fontSize: 11 };

export const Route = createFileRoute("/admin/black-dashboard")({
  head: () => ({
    meta: [
      { title: "Admin · Dashboard BLACK 2026" },
      { name: "description", content: "Leads e vendas da BLACK 2026 · Pós em Dobro." },
      { property: "og:title", content: "Admin · Dashboard BLACK 2026" },
      { property: "og:description", content: "Leads e vendas da BLACK 2026 · Pós em Dobro." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => <AdminGate route="/admin/black-dashboard"><BlackDashboard /></AdminGate>,
});

const META = { leads: 5000, vendas: 765, unicas: 535, assinaturas: 230, ticket: 1800, receita: 765 * 1800 };
const brl = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
const pct = (a: number, b: number) => (b ? `${((a / b) * 100).toFixed(1).replace(".", ",")}%` : "0%");
const fmtDia = (d: string) => d.slice(8, 10) + "/" + d.slice(5, 7);

function BlackDashboard() {
  const fn = useServerFn(getBlackDashboard);
  const [de, setDe] = useState("");
  const [ate, setAte] = useState("");
  const [periodo, setPeriodo] = useState<{ de: string; ate: string } | null>(null);
  const q = useQuery({ queryKey: ["black-dashboard", periodo?.de ?? "", periodo?.ate ?? ""], queryFn: () => fn({ data: periodo ?? {} }), refetchInterval: 60_000 });
  const d = q.data;
  const inp = "rounded-md border border-border bg-card px-2 py-1.5 text-sm text-foreground [color-scheme:dark]";

  return (
    <main className="black2x1-scope relative isolate overflow-hidden min-h-screen bg-black-campaign text-foreground bg-background px-4 py-8 text-foreground"><div aria-hidden className="pointer-events-none absolute -right-32 -top-32 h-[28rem] w-[28rem] rounded-full bg-black-campaign-purple/25 blur-[140px]" /><div aria-hidden className="pointer-events-none absolute -left-32 top-[40rem] h-[24rem] w-[24rem] rounded-full bg-black-campaign-purple/20 blur-[140px]" />
      <div className="relative mx-auto max-w-6xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Admin</Link>
            <span className="mt-4 flex w-fit rounded-full border border-success/40 bg-success/10 px-3 py-1 text-xs font-semibold uppercase text-success">Ação ativa · Captação e vendas pelo CRM</span>
            <h1 className="mt-3 text-3xl font-bold text-foreground sm:text-4xl"><span className="text-black-campaign-lilac">DASHBOARD</span> BLACK 2026 · PÓS EM DOBRO</h1>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">Duas pós pelo preço de uma · captação com grupo de WhatsApp e venda direta para os inscritos.</p>
          </div>
          <button type="button" onClick={() => q.refetch()} className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-semibold">
            <RefreshCw className={`h-4 w-4 ${q.isFetching ? "animate-spin" : ""}`} /> Atualizar
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-black-campaign-purple/40 bg-card/60 px-4 py-3 text-sm">
          <span className="font-semibold">Período:</span>
          <button type="button" onClick={() => { setPeriodo(null); setDe(""); setAte(""); }} className={`rounded-md border px-3 py-1.5 font-semibold ${!periodo ? "border-black-campaign-lilac text-black-campaign-lilac" : "border-border text-muted-foreground"}`}>Total da ação</button>
          <label className="flex items-center gap-2 text-muted-foreground">De <input type="date" value={de} min="2026-10-05" onChange={(e) => setDe(e.target.value)} className={inp} /></label>
          <label className="flex items-center gap-2 text-muted-foreground">Até <input type="date" value={ate} min="2026-10-05" onChange={(e) => setAte(e.target.value)} className={inp} /></label>
          <button type="button" disabled={!de && !ate} onClick={() => setPeriodo({ de, ate: ate && de && ate < de ? de : ate })} className="rounded-md border border-border px-3 py-1.5 font-semibold disabled:opacity-40">Aplicar</button>
          {periodo && <span className="text-xs text-black-campaign-lilac">Mostrando {periodo.de ? periodo.de.split("-").reverse().slice(0, 2).join("/") : "início"} a {periodo.ate ? periodo.ate.split("-").reverse().slice(0, 2).join("/") : "hoje"}</span>}
        </div>


        {q.isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}
        {q.error && <p className="text-sm text-destructive">Erro: {(q.error as Error).message}</p>}

        {d && (
          <>
            <BlackRitmoPanel porDia={d.porDia} vendasDia={d.vendasDia} leadsTotal={d.leads.total} vendas={d.vendas.pagas} receita={d.vendas.receita} entregasPorFonte={d.entregasPorFonte} perfilPorOrigem={d.perfilPorOrigem} listaPlataforma={d.listaPlataforma} porReferencia={d.porReferencia} leadsPorCampanha={d.leadsPorCampanha} periodo={periodo} vendasSlot={<VendasGraficos d={d} />} extra={{ lista: d.leads.lista, entregas: d.leads.entregas, quizz: d.leads.quizz, unicos: d.leads.unicos, daLista: d.vendas.daLista, unica: d.vendas.unica, assinatura: d.vendas.assinatura, receitaUnica: d.vendas.receitaUnica, receitaAss: d.vendas.receitaAss }} />
            <Graficos d={d} />

            <section className="grid gap-8 lg:grid-cols-2">
              <Card title="Leads por dia">
                <Bars full rows={[...d.porDia].reverse().map((r) => ({ k: fmtDia(r.dia), v: r.lista + r.entregas + r.quizz, extra: `${r.lista} lista · ${r.entregas} entregas · ${r.quizz} quizz` }))} />
              </Card>
              <Card title="Vendas por dia">
                <Bars full rows={[...new Set([...d.porDia.map((r) => r.dia), ...d.vendasDia.map((r) => r.dia)])].sort().reverse().map((dia) => d.vendasDia.find((r) => r.dia === dia) ?? { dia, vendas: 0, receita: 0 }).map((r) => ({ k: fmtDia(r.dia), v: r.vendas, extra: brl(r.receita) }))} empty="Nenhuma venda paga ainda" />
              </Card>
              <Card title="Leads por vendedora">
                <Bars rows={d.porVendedora.map(([k, v]) => ({ k, v }))} />
              </Card>
              <Card title="Vendas por canal">
                <Bars rows={d.vendasCanal.map((r) => ({ k: r.canal, v: r.vendas, extra: brl(r.receita) }))} empty="Nenhuma venda paga ainda" />
              </Card>
              <Card title="Leads por origem (ponto de contato)">
                <Bars full top={10} rows={d.porOrigem.map(([k, v]) => ({ k, v }))} />
              </Card>
              <Card title="Vendas por origem">
                <Bars top={10} rows={d.inteligencia.segmentos.filter((s) => s.dim === "Origem" && s.vendas > 0).sort((x, y) => y.vendas - x.vendas || y.leads - x.leads).map((s) => ({ k: s.grupo, v: s.vendas }))} full empty="Nenhuma venda paga ainda" />
              </Card>
              <Card title="Leads por fonte (UTM)">
                <Bars full top={10} rows={d.porFonte.map(([k, v]) => ({ k, v }))} />
              </Card>
              <Card title="Vendas por fonte (UTM)">
                <Bars top={10} rows={d.inteligencia.segmentos.filter((s) => s.dim === "Fonte" && s.vendas > 0).sort((x, y) => y.vendas - x.vendas || y.leads - x.leads).map((s) => ({ k: s.grupo, v: s.vendas }))} full empty="Nenhuma venda paga ainda" />
              </Card>
            </section>
            <div className="flex items-center gap-4 pt-10">
              <h2 className="text-lg font-bold uppercase tracking-[0.15em] text-black-campaign-lilac">Inteligência</h2>
              <span className="h-px flex-1 bg-gradient-to-r from-black-campaign-purple/60 to-transparent" />
            </div>
            <Conversoes d={d} />
            <ComparaListas d={d} />
            <Inteligencia d={d} />
            <div className="flex items-center gap-4 pt-10">
              <h2 className="text-lg font-bold uppercase tracking-[0.15em] text-black-campaign-lilac">Leads da ação</h2>
              <span className="h-px flex-1 bg-gradient-to-r from-black-campaign-purple/60 to-transparent" />
            </div>
            <LeadsTable rows={d.tabela} onDeleted={() => q.refetch()} />
            <div className="flex items-center gap-4 pt-10">
              <h2 className="text-lg font-bold uppercase tracking-[0.15em] text-black-campaign-lilac">Configurações</h2>
              <span className="h-px flex-1 bg-gradient-to-r from-black-campaign-purple/60 to-transparent" />
            </div>
            <BlackTorneiras />
            <BlackGruposConfig />
            <BlackQuizzConfig />
          </>
        )}
      </div>
    </main>
  );
}

function Conversoes({ d }: { d: Dash }) {
  const pc = (v: number) => `${(v * 100).toFixed(1).replace(".", ",")}%`;
  return (
    <div className="grid gap-8 lg:grid-cols-2">
        {["Formação", "Histórico"].map((dim) => {
          const rows = d.inteligencia.segmentos.filter((s) => s.dim === dim).sort((a, b) => b.leads - a.leads);
          return (
            <Card key={dim} title={`Conversão por ${dim.toLowerCase()}`}>
              {rows.length ? <><div className="h-56"><ResponsiveContainer><BarChart data={rows.map((r) => ({ grupo: r.grupo, Leads: r.leads, Vendas: r.vendas }))} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeOpacity={0.1} /><XAxis type="number" {...axis} allowDecimals={false} /><YAxis type="category" dataKey="grupo" {...axis} width={100} tick={({ x, y, payload }: { x: number; y: number; payload: { value: string } }) => <text x={x} y={y} dy={4} textAnchor="end" fill="var(--black-campaign-soft)" fontSize={11}>{payload.value}</text>} />
                <Tooltip {...tip} /><Bar dataKey="Leads" fill={C.purple} /><Bar dataKey="Vendas" fill={C.cyan} />
              </BarChart></ResponsiveContainer></div>
              <table className="mt-4 w-full text-xs"><thead className="text-[10px] uppercase tracking-wide text-muted-foreground"><tr><th className="py-1.5 text-left">Grupo</th><th className="py-1.5 text-center" style={{ color: C.purple }}>Leads</th><th className="py-1.5 text-center" style={{ color: C.cyan }}>Vendas</th><th className="py-1.5 text-center">Conversão</th></tr></thead><tbody>
                {rows.map((r) => <tr key={r.grupo} className="border-t border-border/40"><td className="py-1.5 pr-2">{r.grupo}</td><td className="py-1.5 text-center">{r.leads}</td><td className="py-1.5 text-center">{r.vendas}</td><td className="py-1.5 text-center font-semibold">{r.leads ? pc(r.conv) : "—"}</td></tr>)}
              </tbody></table></> : <p className="text-sm text-muted-foreground">Sem dados ainda</p>}
            </Card>
          );
        })}
      </div>

  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-black-campaign-purple/40 bg-card p-6 sm:p-8">
      <h2 className="mb-3 font-bold">{title}</h2>
      {children}
    </div>
  );
}

function Bars({ rows, empty = "Sem dados ainda", full = false, top }: { rows: { k: string; v: number; extra?: string }[]; empty?: string; full?: boolean; top?: number }) {
  const [aberto, setAberto] = useState(false);
  if (!rows.length) return <p className="text-sm text-muted-foreground">{empty}</p>;
  const max = Math.max(...rows.map((r) => r.v), 1);
  const vis = top && !aberto ? rows.slice(0, top) : rows;
  return (
    <>
    <div className={full ? "space-y-1.5" : "max-h-80 space-y-1.5 overflow-y-auto pr-1"}>
      {vis.map((r) => (
        <div key={r.k} className="text-xs">
          <div className="flex justify-between gap-2"><span className="truncate font-medium">{r.k}</span><span className="shrink-0 text-muted-foreground">{r.v}{r.extra ? ` · ${r.extra}` : ""}</span></div>
          <div className="mt-0.5 h-1.5 rounded-full bg-muted"><div className="h-1.5 rounded-full bg-primary" style={{ width: `${(r.v / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
    {top && rows.length > top && (
      <button type="button" onClick={() => setAberto((a) => !a)} className="mt-4 rounded-md border border-black-campaign-lilac/60 px-3 py-1.5 text-xs font-semibold text-black-campaign-lilac hover:bg-black-campaign-lilac/10">
        {aberto ? "Mostrar só os 10 maiores" : `Ver lista completa (${rows.length})`}
      </button>
    )}
    </>
  );
}

function ComparaListas({ d }: { d: Dash }) {
  const getFn = useServerFn(getBlackDiario);
  const diario = useQuery({ queryKey: ["black-diario"], queryFn: () => getFn() });
  const ult = [...(diario.data ?? [])].reverse().find((r) => r.grupoLista != null || r.grupoEntregas != null || r.grupoQuizz != null);
  const pc = (a: number, b: number) => (b ? `${((a / b) * 100).toFixed(1).replace(".", ",")}%` : "—");
  const calc = (pagina: string, grupo: number | null | undefined) => {
    const r = d.tabela.filter((x) => !x.teste && x.pagina === pagina);
    const n = r.length;
    const vend = r.filter((x) => x.comprouBlack);
    const cont = (f: (x: (typeof r)[number]) => boolean) => r.filter(f).length;
    return [
      ["Leads", n.toLocaleString("pt-BR"), ""],
      ["Formados", cont((x) => x.perfil === "FORMADO").toLocaleString("pt-BR"), pc(cont((x) => x.perfil === "FORMADO"), n)],
      ["Estudantes", cont((x) => x.perfil === "ESTUDANTE").toLocaleString("pt-BR"), pc(cont((x) => x.perfil === "ESTUDANTE"), n)],
      ["Já alunos de pós", cont((x) => x.alunoDe.length > 0).toLocaleString("pt-BR"), pc(cont((x) => x.alunoDe.length > 0), n)],
      ["Já eram leads FEO", cont((x) => x.capturas.length > 1).toLocaleString("pt-BR"), pc(cont((x) => x.capturas.length > 1), n)],
      ["No grupo de WhatsApp", grupo != null ? grupo.toLocaleString("pt-BR") : "—", grupo != null ? pc(grupo, n) : ""],
      ["Com vendedora", cont((x) => !!x.vendedora).toLocaleString("pt-BR"), pc(cont((x) => !!x.vendedora), n)],
      ["Vendas", vend.length.toLocaleString("pt-BR"), pc(vend.length, n)],
      ["Vendas de formados", vend.filter((x) => x.perfil === "FORMADO").length.toString(), pc(vend.filter((x) => x.perfil === "FORMADO").length, cont((x) => x.perfil === "FORMADO"))],
      ["Vendas de estudantes", vend.filter((x) => x.perfil === "ESTUDANTE").length.toString(), pc(vend.filter((x) => x.perfil === "ESTUDANTE").length, cont((x) => x.perfil === "ESTUDANTE"))],
    ] as const;
  };
  const a = calc("/black-lista", ult?.grupoLista);
  const b = calc("/black-lista-entregas", ult?.grupoEntregas);
  const c = calc("/black-quizz", ult?.grupoQuizz);
  const num = (t: string) => (t.endsWith("%") ? parseFloat(t.replace(",", ".")) : NaN);
  const melhor = (i: number, k: number) => { const v = [a, b, c].map((x) => num(x[i][2])); const m = Math.max(...v.filter((x) => !isNaN(x))); return !isNaN(v[k]) && v[k] === m && v.filter((x) => x === m).length === 1; };
  const cols = [{ n: "Lista geral", cor: C.cyan, r: a }, { n: "Entregas", cor: C.purple, r: b }, { n: "Quizz", cor: C.lilac, r: c }];
  return (
    <Recolhivel title="Lista geral × Entregas × Quizz" resumo={`${a[0][1]} × ${b[0][1]} × ${c[0][1]} leads`}>
      <div className="overflow-x-auto"><table className="w-full text-xs">
        <thead className="text-[10px] uppercase tracking-wide text-muted-foreground"><tr><th className="py-1.5 text-left">Métrica</th>{cols.map((x) => <Fragment key={x.n}><th className="py-1.5 text-center" style={{ color: x.cor }}>{x.n}</th><th key={x.n + "%"} className="py-1.5 text-center">%</th></Fragment>)}</tr></thead>
        <tbody>{a.map((row, i) => (
          <tr key={row[0]} className="border-t border-border/40"><td className="py-1.5 pr-2 font-medium">{row[0]}</td>{cols.map((x, k) => <Fragment key={x.n}><td className="py-1.5 text-center font-semibold">{x.r[i][1]}</td><td key={x.n + "%"} className={`py-1.5 text-center ${melhor(i, k) ? "font-bold text-emerald-400" : "text-muted-foreground"}`}>{x.r[i][2]}</td></Fragment>)}</tr>
        ))}</tbody>
      </table></div>
    </Recolhivel>
  );
}

function Inteligencia({ d }: { d: Dash }) {
  const i = d.inteligencia;
  const p = (n: number) => `${(n * 100).toFixed(1).replace(".", ",")}%`;
  const cls = { bom: T.ok, ruim: T.bad, neutro: T.muted };
  return (
    <Recolhivel title="Insights da ação" resumo={`${i.insights.length} insights`}>
        {i.insights.length ? (
          <ul className="flex flex-col gap-3">
            {i.insights.map((x, k) => (
              <li key={k} className="flex items-start gap-2 rounded-md border border-border bg-muted/20 p-3 text-sm">
                <span className={tag(cls[x.tom])}>{x.tom === "bom" ? "Converte mais" : x.tom === "ruim" ? "Atenção" : "Volume"}</span>
                <span>{x.texto} <span className="whitespace-nowrap text-xs text-muted-foreground">({x.leads} leads · {x.vendas} vendas)</span></span>
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-muted-foreground">Ainda não há volume suficiente para tirar conclusões.</p>}
    </Recolhivel>
  );
}

type Dash = Awaited<ReturnType<typeof getBlackDashboard>>;

function Graficos({ d }: { d: Dash }) {
  let accL = 0;
  const leadsDia = d.porDia.map((r) => { accL += r.lista + r.entregas + r.quizz; return { dia: fmtDia(r.dia), Lista: r.lista, Entregas: r.entregas, Quizz: r.quizz, Acumulado: accL }; });
  let accR = 0;
  const vendasDia = [...new Set([...d.porDia.map((r) => r.dia), ...d.vendasDia.map((r) => r.dia)])].sort().map((dia) => d.vendasDia.find((r) => r.dia === dia) ?? { dia, vendas: 0, receita: 0 }).map((r) => { accR += r.receita; return { dia: fmtDia(r.dia), Vendas: r.vendas, Faturamento: r.receita, Acumulado: accR }; });
  const vend = d.porVendedora.length ? d.porVendedoraFull.map((r) => ({ ...r, conv: r.leads ? +((r.vendas / r.leads) * 100).toFixed(1) : 0 })) : [];
  const pc = (n: number) => `${(n * 100).toFixed(1).replace(".", ",")}%`;
  const segDe = (dim: string, nome: string) => {
    const est = /estud/i.test(nome);
    return d.inteligencia.segmentos.find((s) => s.dim === dim && (/estud/i.test(s.grupo) === est));
  };
  const pies: { t: string; dim?: string; data: { name: string; value: number }[] }[] = [
    { t: "Única × Assinatura", data: [{ name: "Única", value: d.vendas.unica }, { name: "Assinatura", value: d.vendas.assinatura }] },
    { t: "Formação", dim: "Formação", data: d.porFormacao.map(([name, value]) => ({ name: name.replace(/^Sou /, ""), value })) },
    { t: "Lista × Entregas × Quizz", data: [{ name: "Lista", value: d.leads.lista }, { name: "Entregas", value: d.leads.entregas }, { name: "Quizz", value: d.leads.quizz }] },
  ];
  return (
    <section className="space-y-10">
      <div className="grid gap-8 lg:grid-cols-2">
        <Card title="Leads por dia (lista, entregas e quizz) e acumulado">
          <div className="h-64"><ResponsiveContainer><ComposedChart data={leadsDia} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
            <CartesianGrid strokeOpacity={0.1} /><XAxis dataKey="dia" {...axis} /><YAxis yAxisId="l" {...axis} width={40} /><YAxis yAxisId="r" orientation="right" {...axis} width={40} />
            <Tooltip {...tip} /><Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="l" dataKey="Lista" stackId="a" fill={C.purple} /><Bar yAxisId="l" dataKey="Entregas" stackId="a" fill={C.cyan} /><Bar yAxisId="l" dataKey="Quizz" stackId="a" fill={C.lilac} />
            <Line yAxisId="r" dataKey="Acumulado" stroke={C.lilac} strokeWidth={2} dot />
          </ComposedChart></ResponsiveContainer></div>
        </Card>
        <Card title="Vendas e faturamento por dia">
          {vendasDia.length ? <div className="h-64"><ResponsiveContainer><ComposedChart data={vendasDia} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
            <CartesianGrid strokeOpacity={0.1} /><XAxis dataKey="dia" {...axis} /><YAxis yAxisId="l" {...axis} width={40} allowDecimals={false} /><YAxis yAxisId="r" orientation="right" {...axis} width={40} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
            <Tooltip {...tip} formatter={(v: number, n: string) => (n === "Vendas" ? v : brl(v))} /><Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="l" dataKey="Vendas" fill={C.purple} /><Line yAxisId="r" dataKey="Faturamento" stroke={C.cyan} strokeWidth={2} />
          </ComposedChart></ResponsiveContainer></div> : <p className="text-sm text-muted-foreground">Nenhuma venda paga ainda</p>}
        </Card>
      </div>
    </section>
  );
}

const FIM26 = "2026-11-03";
const ABERTURA26 = "2026-10-26";
const dAcaoDe = (ddmm: string) => { const [dd, mm] = ddmm.split("/").map(Number); return `D${Math.round((Date.UTC(2026, mm - 1, dd) - Date.UTC(2026, 9, 5)) / 86400_000) + 1}`; };
const tickAcao = ({ x, y, payload }: { x: number; y: number; payload: { value: string } }) => (
  <g transform={`translate(${x},${y})`}><text dy={12} textAnchor="middle" fill="var(--black-campaign-soft)" fontSize={11}>{payload.value}</text><text dy={26} textAnchor="middle" fill="var(--black-campaign-lilac)" fontSize={10}>{dAcaoDe(payload.value)}</text></g>
);
const addDia = (d: string, n: number) => new Date(Date.parse(d + "T12:00:00Z") + n * 86400_000).toISOString().slice(0, 10);

function VendasGraficos({ d }: { d: Dash }) {
  const porDia = new Map(d.vendasDia.map((r) => [r.dia, r]));
  const primeiro = d.vendasDia[0]?.dia ?? ABERTURA26;
  const dias: string[] = [];
  for (let x = primeiro; x <= FIM26; x = addDia(x, 1)) dias.push(x);
  const hoje = new Date(Date.now() - 3 * 3600_000).toISOString().slice(0, 10);
  const acao = dias.map((dia) => { const r = porDia.get(dia); const fut = dia > hoje; return { dia: fmtDia(dia), Únicas: fut ? null : r?.unicas ?? 0, Assinaturas: fut ? null : r?.assinaturas ?? 0, Faturamento: fut ? null : r?.receita ?? 0 }; });

  // Alinhamento pela abertura do carrinho: 26/10/2026 ↔ 27/10/2025.
  const ab26 = Math.round((Date.parse(ABERTURA26) - Date.parse("2026-10-05")) / 86400_000);
  const ab25 = BLACK25.findIndex((b) => b.dia === BLACK25_ABERTURA_DIA);
  const comp: { dia: string; n: number; rel: number; dia25: string; "2026": number | null; "2025": number | null; fat26: number | null; fat25: number | null }[] = [];
  for (let i = 0; i < 29; i++) {
    const dia = addDia("2026-10-05", i);
    if (dia > FIM26) break;
    const r = porDia.get(dia); const b = BLACK25[i - ab26 + ab25];
    comp.push({ dia: fmtDia(dia), n: i + 1, rel: i - ab26, dia25: b?.dia ?? "—", "2026": dia > hoje ? null : r?.vendas ?? 0, "2025": b?.vendas ?? null, fat26: dia > hoje ? null : r?.receita ?? 0, fat25: b?.receita ?? null });
  }

  // Comparativo por hora: D1 = abertura do carrinho (26/10/2026 ↔ 27/10/2025), até o fechamento (02/11).
  const diasVenda: string[] = [];
  for (let x = ABERTURA26; x <= FIM26; x = addDia(x, 1)) diasVenda.push(x);
  const diaPadrao = hoje < ABERTURA26 ? ABERTURA26 : hoje > FIM26 ? FIM26 : hoje;
  const [diaH, setDiaH] = useState(diaPadrao);
  const [acum, setAcum] = useState(false);
  const idxDia = Math.max(0, diasVenda.indexOf(diaH));
  const dia25Iso = addDia("2025-10-27", idxDia); // dia equivalente de 2025 (D1 = 27/10/2025)
  const dia25 = fmtDia(dia25Iso);
  const b25 = BLACK25.find((b) => b.dia === dia25);
  const ehAbertura = diaH === ABERTURA26;
  const h26: number[] = d.vendasHora[diaH] ?? Array(24).fill(0);
  // 2025 real (Eduzz) por dia; na abertura, cai para os números do placar de MKT se o banco ainda não tiver o histórico.
  const h25real = d.vendasHora25?.[dia25Iso];
  const h25: Record<number, number> = h25real
    ? Object.fromEntries(h25real.map((v, h) => [h, v]).filter(([, v]) => (v as number) > 0)) as Record<number, number>
    : ehAbertura ? BLACK25_ABERTURA_POR_HORA : {};
  const tem25 = Object.keys(h25).length > 0;
  const agora = new Date(Date.now() - 3 * 3600_000);
  const ehHoje = diaH === hoje;
  const horaAgora = ehHoje ? agora.getUTCHours() : 23;
  const minAgora = ehHoje ? agora.getUTCMinutes() : 60;
  const antes8 = h26.slice(0, 8).reduce((a, b) => a + b, 0);
  const antes8_25 = Object.entries(h25).filter(([h]) => Number(h) < 8).reduce((a, [, v]) => a + v, 0);
  let s26 = antes8, s25 = antes8_25;
  const hora = [{ hora: "antes 08h", h: 7 }, ...Array.from({ length: 16 }, (_, k) => ({ hora: `${String(k + 8).padStart(2, "0")}h`, h: k + 8 }))].map(({ hora, h }) => {
    const v26 = h < 8 ? antes8 : h26[h]; const v25 = h < 8 ? antes8_25 : h25[h] ?? 0;
    if (h >= 8) s26 += v26; s25 += v25;
    const fut = h > horaAgora;
    return { hora: h === horaAgora && ehHoje ? `${hora}*` : hora, "2026": fut ? null : acum ? s26 : v26, "2025": tem25 ? (acum ? s25 : v25) : null };
  });
  const tot26 = h26.reduce((a, b) => a + b, 0);
  const tot25 = Object.values(h25).reduce((a, b) => a + b, 0);
  const totDia = porDia.get(diaH)?.vendas ?? tot26;
  const placar = comparativoAteAgora(h26, h25, horaAgora, minAgora);
  const difPlacar = placar.v25 ? `${(((placar.v26 - placar.v25) / placar.v25) * 100).toFixed(0)}%` : "—";

  return (
    <div className="space-y-8">
      <Card title="Vendas da ação: dia a dia até o fechamento do carrinho (03/11)">
        <div className="h-72"><ResponsiveContainer><ComposedChart data={acao} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
          <CartesianGrid strokeOpacity={0.1} /><XAxis dataKey="dia" {...axis} height={44} interval={0} tick={tickAcao} /><YAxis yAxisId="l" {...axis} width={40} allowDecimals={false} /><YAxis yAxisId="r" orientation="right" {...axis} width={44} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
          <Tooltip {...tip} formatter={(v: number, n: string) => (n === "Faturamento" ? brl(v) : v)} /><Legend wrapperStyle={{ fontSize: 12 }} />
          <ReferenceLine yAxisId="l" x={fmtDia(ABERTURA26)} stroke={C.lilac} strokeDasharray="4 4" label={{ value: "Abertura", fill: C.soft, fontSize: 11, position: "top" }} />
          <Bar yAxisId="l" dataKey="Únicas" stackId="v" fill={C.purple} /><Bar yAxisId="l" dataKey="Assinaturas" stackId="v" fill={C.cyan} />
          <Line yAxisId="r" dataKey="Faturamento" stroke={C.lilac} strokeWidth={2} connectNulls={false} />
        </ComposedChart></ResponsiveContainer></div>
      </Card>
      <Card title="Vendas por dia: 2026 × 2025 (mesmo dia da ação)">
        <div className="h-72"><ResponsiveContainer><ComposedChart data={comp} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid strokeOpacity={0.1} /><XAxis dataKey="dia" {...axis} height={44} interval={0} tick={tickAcao} /><YAxis {...axis} width={40} allowDecimals={false} />
          <Tooltip {...tip} content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const p = payload[0].payload as (typeof comp)[number];
            const dif = p["2026"] != null && p["2025"] ? `${(((p["2026"] - p["2025"]) / p["2025"]) * 100).toFixed(0)}%` : "—";
            const rel = p.rel === 0 ? "Abertura" : `Abertura ${p.rel > 0 ? "+" : ""}${p.rel}`;
            return <div style={tip.contentStyle} className="p-2 text-xs"><p className="font-semibold">{rel} · {p.dia}/2026 × {p.dia25}/2025</p><p>2026: {p["2026"] ?? "—"} vendas · {p.fat26 != null ? brl(p.fat26) : "—"}</p><p>2025: {p["2025"] ?? "—"} vendas · {p.fat25 != null ? brl(p.fat25) : "—"}</p><p>Diferença: {dif}</p></div>;
          }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <ReferenceLine x={fmtDia(ABERTURA26)} stroke={C.lilac} strokeDasharray="4 4" label={{ value: "Abertura 2026", fill: C.soft, fontSize: 11, position: "top" }} />
          <Line dataKey="2026" stroke={C.cyan} strokeWidth={2} dot connectNulls={false} /><Line dataKey="2025" stroke={C.purple} strokeWidth={2} strokeDasharray="5 3" dot={false} />
        </ComposedChart></ResponsiveContainer></div>
      </Card>
      <Card title={`Vendas por hora: D${idxDia + 1} de carrinho aberto · ${fmtDia(diaH)}/2026 × ${dia25}/2025`}>
        {ehHoje && <div className="mb-3 rounded-md border border-primary/40 bg-primary/10 p-3 text-sm">
          {tem25 ? (
            <><span className="font-semibold">Até agora ({String(horaAgora).padStart(2, "0")}:{String(minAgora).padStart(2, "0")}):</span> 2026 = <b>{placar.v26}</b> vendas · 2025 no mesmo horário = <b>{placar.v25.toLocaleString("pt-BR")}</b> · diferença <b>{difPlacar}</b></>
          ) : (
            <><span className="font-semibold">Até agora ({String(horaAgora).padStart(2, "0")}:{String(minAgora).padStart(2, "0")}):</span> 2026 = <b>{placar.v26}</b> vendas · {dia25}/2025 fechou o dia com <b>{b25?.vendas ?? "—"}</b> vendas</>
          )}
          <span className="ml-2 text-xs text-muted-foreground">atualiza a cada minuto</span>
        </div>}
        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-muted-foreground">Dia (carrinho aberto):</span>
          {diasVenda.map((x, i) => (
            <button key={x} onClick={() => setDiaH(x)} title={`${fmtDia(x)}/2026 × ${fmtDia(addDia("2025-10-27", i))}/2025`} className={`rounded-md border px-2 py-1 ${x === diaH ? "border-primary bg-primary/20" : "border-border"}`}>D{i + 1} · {fmtDia(x)}</button>
          ))}
          <span className="ml-2 text-muted-foreground">Ver:</span>
          <button onClick={() => setAcum(false)} className={`rounded-md border px-2 py-1 ${!acum ? "border-primary bg-primary/20" : "border-border"}`}>Por hora</button>
          <button onClick={() => setAcum(true)} className={`rounded-md border px-2 py-1 ${acum ? "border-primary bg-primary/20" : "border-border"}`}>Acumulado</button>
          <span className="ml-auto text-muted-foreground">{fmtDia(diaH)}/2026: {tot26} vendas no gráfico{tem25 ? ` · ${dia25}/2025: ${tot25} vendas` : b25 ? ` · ${dia25}/2025 (dia inteiro): ${b25.vendas} vendas` : ""}</span>
        </div>
        <div className="h-72"><ResponsiveContainer><ComposedChart data={hora} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid strokeOpacity={0.1} strokeDasharray="3 3" /><XAxis dataKey="hora" {...axis} interval={0} /><YAxis {...axis} width={40} allowDecimals={false} />
          <Tooltip {...tip} /><Legend wrapperStyle={{ fontSize: 12 }} />
          <Line dataKey="2026" name={`${fmtDia(diaH)}/2026 (D${idxDia + 1})`} stroke={C.cyan} strokeWidth={2} dot connectNulls={false} />
          {tem25 && <Line dataKey="2025" name={`${dia25}/2025 (D${idxDia + 1})${h25real ? "" : " · placar MKT"}`} stroke={C.purple} strokeWidth={2} strokeDasharray="5 3" dot />}
        </ComposedChart></ResponsiveContainer></div>
        <p className="mt-2 text-xs text-muted-foreground">Cada ponto soma as vendas de HH:00 a HH:59 (horário de Brasília). {ehHoje && "A hora com * está em andamento. "}{tem25 ? (h25real ? "A linha de 2025 vem das vendas reais da Eduzz." : "A linha de 2025 vem do placar de MKT (dia de abertura).") + " No placar, a hora atual de 2025 entra proporcional aos minutos." : `Ainda sem o detalhe por hora de ${dia25}/2025; o dia fechou com ${b25?.vendas ?? "—"} vendas.`} Após o fechamento do carrinho (03/11), as vendas continuam só pelo CRM e entram nos gráficos por dia.</p>
        {totDia !== tot26 && <p className="mt-1 text-xs text-muted-foreground">{totDia - tot26} venda(s) do dia são externas, sem horário, e ficam fora deste gráfico (total do dia: {totDia}).</p>}
      </Card>
    </div>
  );
}
